import React from 'react';
import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@mui/material';
import { formatMoney } from '../../services/billingApi';
import { BillingPanel, BillingTableHead, billingSx, tones } from './billingUi';

/**
 * Unified Product / Sub-Product cost grid.
 * @param embedded — skip outer panel (e.g. inside License card)
 */
const ProductCostGrid = ({ grid, dense = false, embedded = false }) => {
  if (!grid) {
    return (
      <Typography color="text.secondary" variant="body2">
        No product cost data.
      </Typography>
    );
  }

  const rows = grid.rows || [];
  const currency = grid.currency || 'USD';

  const table = (
      <TableContainer>
        <Table size={dense ? 'small' : 'medium'}>
          <BillingTableHead
            columns={[
              { label: 'Product' },
              { label: 'Sub-product' },
              { label: 'Kind' },
              { label: 'On' },
              { label: 'Qty', align: 'right' },
              { label: 'Used', align: 'right' },
              { label: 'Unit' },
              { label: 'Unit price', align: 'right' },
              { label: 'Cost', align: 'right' },
            ]}
          />
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} sx={{ py: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">
                    No product components. Restart backend to seed Product Master.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => (
                <TableRow
                  key={`${r.productCode}-${r.componentCode}`}
                  sx={{
                    opacity: r.productEnabled ? 1 : 0.42,
                    bgcolor:
                      r.componentKind === 'BASE' ? 'rgba(139, 21, 56, 0.03)' : undefined,
                    '&:last-child td': { borderBottom: 0 },
                  }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {r.productName || r.productCode}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{r.componentName}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {[r.componentCode, r.chargeCode && `charge ${r.chargeCode}`]
                        .filter(Boolean)
                        .join(' · ')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={r.componentKind || '—'}
                      variant="outlined"
                      sx={{ height: 22, fontSize: '0.68rem', borderColor: 'divider' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      color={r.productEnabled ? 'success.dark' : 'text.disabled'}
                    >
                      {r.productEnabled ? 'Yes' : 'No'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {r.quantity != null ? r.quantity : '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {r.usedQuantity != null ? r.usedQuantity : '—'}
                  </TableCell>
                  <TableCell>{r.unit || '—'}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {r.unitPrice != null ? formatMoney(r.unitPrice, currency) : '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                    {r.lineCost != null ? formatMoney(r.lineCost, currency) : '—'}
                  </TableCell>
                </TableRow>
              ))
            )}
            <TableRow sx={{ bgcolor: tones.teal.soft }}>
              <TableCell colSpan={8} align="right" sx={{ py: 1.75 }}>
                <Typography variant="body2" fontWeight={650} sx={{ color: tones.teal.text }}>
                  Estimated total (enabled products)
                </Typography>
              </TableCell>
              <TableCell align="right" sx={{ py: 1.75, fontVariantNumeric: 'tabular-nums' }}>
                <Typography variant="body2" fontWeight={700} sx={{ color: tones.teal.main }}>
                  {formatMoney(grid.grandTotal, currency)}
                </Typography>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
  );

  if (embedded) {
    return (
      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}>
        {table}
      </Box>
    );
  }

  return (
    <BillingPanel>
      {table}
      {grid.subscriptionId ? (
        <Box sx={{ ...billingSx.panelPad, pt: 1.25, pb: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary">
            Subscription #{grid.subscriptionId} · {currency}
          </Typography>
        </Box>
      ) : null}
    </BillingPanel>
  );
};

export default ProductCostGrid;
